import {
  Button,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import useLeaveRequestFormInputsViewModel from './viewModel'
import { Controller } from 'react-hook-form'
import { AccountTeam } from '../../../../../database/enums/AccountTeam.enum'
import AccountTeamEnumHelper from '../../../../../database/enums/helpers/account-team.helper'
import { LeaveTime } from '../../../../../database/enums/LeaveTime.enum'
import LeaveTimeEnumHelper from '../../../../../database/enums/helpers/leave-time.helper'
import { DatePicker } from '@mui/x-date-pickers'
import { LeaveType } from '../../../../../database/enums/LeaveType.enum'
import LeaveTypeEnumHelper from '../../../../../database/enums/helpers/leave-type.helper '
import VisuallyHiddenInput from '../../../../../components/ui/VisuallyHiddenInput'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LeaveRequestFormInputsProps } from './props'
import { Fragment } from 'react/jsx-runtime'

export default function LeaveRequestFormInputs(props: LeaveRequestFormInputsProps) {
  const { scopeNamePrefix } = props
  const viewModel = useLeaveRequestFormInputsViewModel()

  const getFileUploadedPreviewLabel = (value?: File | null) => {
    if (!!value) return `${value.name} (${Intl.NumberFormat().format(value.size / (1024 * 1024))}MB)`
    return ''
  }

  return (
    <Fragment>
      <CardContent>
        <Stack gap={2}>
          <Controller
            control={viewModel.formHook.control}
            name='name'
            render={({ field, fieldState }) => (
              <TextField
                variant='outlined'
                required
                label='Name'
                placeholder='Enter your name...'
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={viewModel.formHook.control}
            name='email'
            render={({ field, fieldState }) => (
              <TextField
                variant='outlined'
                required
                label='Email'
                placeholder='Enter your email...'
                type='email'
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={viewModel.formHook.control}
            name='accountTeam'
            render={({ field }) => (
              <FormControl fullWidth required sx={{ px: 1 }}>
                <FormLabel id={`${scopeNamePrefix}-team-label`}>Team</FormLabel>
                <RadioGroup row {...field}>
                  {Object.values(AccountTeam).map((item, index) => (
                    <FormControlLabel
                      key={`${scopeNamePrefix}-team-option-${index}`}
                      value={item}
                      control={<Radio />}
                      label={AccountTeamEnumHelper.getLabel(item)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          <Controller
            control={viewModel.formHook.control}
            name='position'
            render={({ field, fieldState }) => (
              <TextField
                variant='outlined'
                required
                label='Position'
                placeholder='Enter your position...'
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </CardContent>
      <Divider />
      <CardContent>
        <Stack gap={2}>
          <Controller
            control={viewModel.formHook.control}
            name='leaveTime'
            render={({ field }) => (
              <FormControl fullWidth required sx={{ px: 1 }}>
                <FormLabel id={`${scopeNamePrefix}-leaveTime-label`}>Leave Time</FormLabel>
                <RadioGroup row {...field}>
                  {Object.values(LeaveTime).map((item, index) => (
                    <FormControlLabel
                      key={`${scopeNamePrefix}-leaveTime-option-${index}`}
                      value={item}
                      control={<Radio />}
                      label={LeaveTimeEnumHelper.getLabel(item)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Controller
              control={viewModel.formHook.control}
              name='startDate'
              render={({ field, fieldState }) => (
                <DatePicker
                  label='Start Date'
                  {...field}
                  onChange={newVal => {
                    const endDate = viewModel.formHook.getValues('endDate')
                    field.onChange(newVal)
                    if (!!newVal && newVal > endDate)
                      viewModel.formHook.setValue('endDate', newVal, { shouldValidate: true })
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              )}
            />
            <Controller
              control={viewModel.formHook.control}
              name='endDate'
              render={({ field, fieldState }) => (
                <DatePicker
                  label='End Date'
                  minDate={viewModel.formHook.watch('startDate')}
                  {...field}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              )}
            />
          </Stack>
          <Controller
            control={viewModel.formHook.control}
            name='leaveType'
            render={({ field }) => (
              <FormControl fullWidth required>
                <InputLabel id={`${scopeNamePrefix}-leaveType-label`}>Type of leave</InputLabel>
                <Select
                  labelId={`${scopeNamePrefix}-leaveType-label`}
                  label='Type of leave'
                  {...field}
                  onChange={newValue => {
                    field.onChange(newValue)
                    viewModel.formHook.trigger('otherLeaveType')
                  }}
                >
                  {Object.values(LeaveType).map((item, index) => (
                    <MenuItem key={`${scopeNamePrefix}-leaveType-option-${index}`} value={item}>
                      {LeaveTypeEnumHelper.getLabel(item)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {viewModel.formHook.watch('leaveType') === LeaveType.OTHER ? (
            <Controller
              control={viewModel.formHook.control}
              name='otherLeaveType'
              render={({ field }) => (
                <TextField
                  variant='outlined'
                  required
                  label='Other Leave Type'
                  placeholder='Enter your leave type...'
                  {...field}
                />
              )}
            />
          ) : null}
        </Stack>
      </CardContent>
      <Divider />
      <CardContent>
        <Stack gap={2}>
          <Controller
            control={viewModel.formHook.control}
            name='supportingDocument'
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!fieldState.error}>
                <FormLabel sx={{ mb: 2 }}>
                  <Typography mb={2}>Supporting Documents</Typography>
                  <Typography variant='caption'>Upload 1 supported file: PDF or image. Max 1 GB.</Typography>
                </FormLabel>
                <Stack direction={'row'} gap={2} alignItems={'center'} flexWrap={'wrap'}>
                  <Button
                    component='label'
                    role={undefined}
                    variant='contained'
                    tabIndex={-1}
                    startIcon={<FileUploadIcon />}
                  >
                    Add File
                    <VisuallyHiddenInput
                      type='file'
                      accept='.pdf, image/*'
                      onChange={e => {
                        if (!!e.target.files && !!e.target.files[0]) field.onChange(e.target.files[0])
                      }}
                    />
                  </Button>
                  <Typography variant='subtitle2' color='primary.main'>
                    {getFileUploadedPreviewLabel(field.value)}
                  </Typography>
                </Stack>
              </FormControl>
            )}
          />
          <Controller
            control={viewModel.formHook.control}
            name='reason'
            render={({ field }) => (
              <TextField
                variant='outlined'
                label='Reason for leave'
                placeholder='Enter your reasons...'
                multiline
                minRows={3}
                {...field}
              />
            )}
          />
        </Stack>
      </CardContent>
    </Fragment>
  )
}
